import { AnyAction } from "redux";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { DATOS } from "~constants/endosos";
import { DEBITO_CBU, DEBITO_TARJETA_CREDITO } from "~constants/formaPago";
import {
  GetPolizaResponse,
  PostSolicitarEndosoRequest,
  PutAplicarEndosoRequest,
  PutRechazarEndosoRequest,
} from "~api/types";
import { Campo, Dispatch, FiltroValue, GetState, Services } from "../types";

const baseDetailPoliza = "detallePoliza";
const baseEndosoPoliza = "endosoPoliza";

interface DetallePolizaNuevoState {
  currentPoliza: GetPolizaResponse | null;
  isLoading: boolean;
  failed: boolean;
  submitting: boolean;
  polizaHasChanged: boolean;
  polizaUpdated: boolean;
  tipo: FiltroValue;
  tabSelected: number;

  //Datos Generales
  coberturaSelected: Campo;
  premioTotalSelected: Campo;
  formaPagoSelected: Campo;
  cbuSelected: Campo;
  tarjetaCreditoSelected: Campo;
  marcaTarjetaCreditoSelected: Campo;
  //Datos Anulacion
  motivoAnulacionSelected: Campo;
  //Datos Asegurado
  emailSelected: Campo;
  telefonoSelected: Campo;
  domicilioSelected: Campo;
  //Datos Vehiculo
  anioSelected: Campo;
  marcaSelected: Campo;
  modeloSelected: Campo;
  codiaSelected: Campo;
  provinciaSelected: Campo;
  localidadSelected: Campo;
  codigoPostalSelected: Campo;
  dominioSelected: Campo;
  numeroMotorSelected: Campo;
  numeroChasisSelected: Campo;
  tieneGNC: Campo;

  urlPolizaSelected: Campo;
  urlTarjetaSelected: Campo;
  urlCedulaMercosurSelected: Campo;
  urlCuponeraSelected: Campo;

  /** Lo escribe `setCotizacionId`; no forma parte del estado inicial. */
  cotizacionId?: Campo;
}

/**
 * Las acciones de esta sección llevan su propio reductor adentro, en vez de
 * registrarse en un handleActions: el reducer sólo comprueba que el type sea
 * de esta sección y delega.
 */
interface DetallePolizaAction<P = undefined> {
  type: string;
  payload?: P;
  reducer: (
    state: DetallePolizaNuevoState,
    action: DetallePolizaAction<P>
  ) => DetallePolizaNuevoState;
}

const detallePolizaInitialState: DetallePolizaNuevoState = {
  currentPoliza: null,
  isLoading: false,
  failed: false,
  submitting: false,
  polizaHasChanged: false,
  polizaUpdated: false,
  tipo: DATOS,
  tabSelected: 0,

  //Datos Generales
  coberturaSelected: { value: null, valid: true, focus: false },
  premioTotalSelected: { value: undefined, valid: true, focus: false },
  formaPagoSelected: { value: null, valid: true, focus: false },
  cbuSelected: { value: null, valid: true, focus: false },
  tarjetaCreditoSelected: { value: null, valid: true, focus: false },
  marcaTarjetaCreditoSelected: { value: null, valid: true, focus: false },
  //Datos Anulacion
  motivoAnulacionSelected: {
    value: null,
    valid: true,
    focus: false,
  },
  //Datos Asegurado
  emailSelected: { value: null, valid: true, focus: false },
  telefonoSelected: { value: null, valid: true, focus: false },
  domicilioSelected: { value: null, valid: true, focus: false },
  //Datos Vehiculo
  anioSelected: { value: null, valid: true, focus: false },
  marcaSelected: { value: null, valid: true, focus: false },
  modeloSelected: { value: null, valid: true, focus: false },
  codiaSelected: { value: null, valid: true, focus: false },
  provinciaSelected: { value: null, valid: true, focus: false },
  localidadSelected: { value: null, valid: true, focus: false },
  codigoPostalSelected: { value: null, valid: true, focus: false },
  dominioSelected: { value: null, valid: true, focus: false },
  numeroMotorSelected: { value: null, valid: true, focus: false },
  numeroChasisSelected: { value: null, valid: true, focus: false },
  tieneGNC: { value: null, valid: true, focus: false },

  urlPolizaSelected: { value: null, valid: true, focus: false },
  urlTarjetaSelected: { value: null, valid: true, focus: false },
  urlCedulaMercosurSelected: { value: null, valid: true, focus: false },
  urlCuponeraSelected: { value: null, valid: true, focus: false },
};

export const detallePolizaNuevoReducer = (
  state: DetallePolizaNuevoState = detallePolizaInitialState,
  action: AnyAction
): DetallePolizaNuevoState => {
  const isThisReducer =
    action.type.includes(baseDetailPoliza) ||
    action.type.includes(baseEndosoPoliza);
  if (action.reducer && isThisReducer) return action.reducer(state, action);

  return state;
};

export const clear = (): DetallePolizaAction<{}> => {
  return {
    type: `${baseEndosoPoliza}/clear`,
    payload: {},
    reducer: (state) => ({
      ...detallePolizaInitialState,
      tabSelected: state.tabSelected,
    }),
  };
};
export const clearChanges = (): DetallePolizaAction<{}> => {
  return {
    type: `${baseEndosoPoliza}/clearChanges`,
    payload: {},
    reducer: (state) => {
      const getFormatted = (value: any): Campo => ({
        value: value,
        valid: true,
        focus: false,
      });
      const currentPoliza = state.currentPoliza!;
      return {
        ...state,
        polizaHasChanged: false,

        coberturaSelected: getFormatted(currentPoliza.coberturaId),
        premioTotalSelected: getFormatted(currentPoliza.datosPlan.premioTotal),
        formaPagoSelected: getFormatted(currentPoliza.datosPlan.formaPago),
        //Datos vehiculo
        anioSelected: getFormatted(currentPoliza.datosVehiculo.anio),
        codiaSelected: getFormatted(currentPoliza.datosVehiculo.codia),
        codigoPostalSelected: getFormatted(
          currentPoliza.datosVehiculo.codigoPostal
        ),
        dominioSelected: getFormatted(currentPoliza.datosVehiculo.dominio),
        localidadSelected: getFormatted(
          currentPoliza.datosVehiculo.nombreLocalidad
        ),
        marcaSelected: getFormatted(currentPoliza.datosVehiculo.marca),
        modeloSelected: getFormatted(currentPoliza.datosVehiculo.modelo),
        numeroChasisSelected: getFormatted(
          currentPoliza.datosVehiculo.numeroChasis
        ),
        numeroMotorSelected: getFormatted(
          currentPoliza.datosVehiculo.numeroMotor
        ),
        // El endoso espera el código de provincia y el detalle solo trae el nombre
        provinciaSelected: getFormatted(null),
        tieneGNC: getFormatted(currentPoliza.datosVehiculo.tieneGNC),
        //Datos Asegurado
        emailSelected: getFormatted(currentPoliza.asegurado.email),
        telefonoSelected: getFormatted(
          // Un celular vacío también pasa al fijo, como en siniestros.
          currentPoliza.asegurado.telefonoCelular ||
            currentPoliza.asegurado.telefonoFijo
        ),
        domicilioSelected: getFormatted(currentPoliza.asegurado.domicilio),
        //certificados
        urlCedulaMercosurSelected: getFormatted(
          currentPoliza.certificados.ubicacionCedulaMercosur
        ),
        urlCuponeraSelected: getFormatted(
          currentPoliza.certificados.ubicacionCuponera
        ),
        urlPolizaSelected: getFormatted(
          currentPoliza.certificados.ubicacionPoliza
        ),
        urlTarjetaSelected: getFormatted(
          currentPoliza.certificados.ubicacionTarjeta
        ),
      };
    },
  };
};

export const setTipo = (value: FiltroValue): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setTipo`,
    reducer: (state) => ({
      ...state,
      tipo: value,
      submitting: false,
    }),
  };
};

export const setTabSelected = (value: number): DetallePolizaAction => {
  return {
    type: `${baseDetailPoliza}/setTabSelected`,
    reducer: (state) => ({
      ...state,
      tabSelected: value,
    }),
  };
};

//Datos Generales
export const setCobertura = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCobertura`,
    reducer: (state) => ({
      ...state,
      coberturaSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setPremio = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setPremio`,
    reducer: (state) => ({
      ...state,
      premioTotalSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setCotizacionId = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCotizacionId`,
    reducer: (state) => ({
      ...state,
      cotizacionId: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

/**
 * Pasar de débito a otro medio de pago (o al revés) cambia el precio, así que
 * obliga a volver a elegir la cobertura.
 */
const getPaymentMethodChanged = (prev: unknown, newValue: unknown) => {
  const debito: unknown[] = [DEBITO_CBU, DEBITO_TARJETA_CREDITO];
  if (debito.includes(prev) && !debito.includes(newValue)) return true;
  if (!debito.includes(prev) && debito.includes(newValue)) return true;
  return false;
};

export const setFormaPago = (value: Campo): DetallePolizaAction<Campo> => {
  return {
    type: `${baseEndosoPoliza}/setFormaPago`,
    payload: value,
    reducer: (state) => ({
      ...state,
      formaPagoSelected: { ...value, endosado: true },
      // Sólo puede invalidar: si otro cambio ya obligó a re-cotizar, cambiar la
      // forma de pago no debe volver a dar por buena la cobertura vieja.
      coberturaSelected: {
        ...state.coberturaSelected,
        valid:
          Boolean(state.coberturaSelected.valid) &&
          !getPaymentMethodChanged(state.formaPagoSelected.value, value.value),
      },
      polizaHasChanged: true,
    }),
  };
};
export const setCBU = (value: Campo): DetallePolizaAction<Campo> => {
  return {
    type: `${baseEndosoPoliza}/setCBU`,
    payload: value,
    reducer: (state) => ({
      ...state,
      cbuSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};
export const setTarjetaCredito = ({
  tarjetaCredito,
  marcaTarjetaCredito,
}: {
  tarjetaCredito: Campo;
  marcaTarjetaCredito: Campo;
}): DetallePolizaAction<{
  tarjetaCredito: Campo;
  marcaTarjetaCredito: Campo;
}> => {
  return {
    type: `${baseEndosoPoliza}/setTarjetaCredito`,
    payload: { tarjetaCredito, marcaTarjetaCredito },
    reducer: (state) => ({
      ...state,
      tarjetaCreditoSelected: { ...tarjetaCredito, endosado: true },
      marcaTarjetaCreditoSelected: { ...marcaTarjetaCredito, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

//Anulacion
export const setMotivoAnulacion = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setMotivoAnulacion`,
    reducer: (state) => ({
      ...state,
      motivoAnulacionSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

//Datos Asegurado
export const setEmail = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setEmail`,
    reducer: (state) => ({
      ...state,
      emailSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setTelefono = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setTelefono`,
    reducer: (state) => ({
      ...state,
      telefonoSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setDomicilio = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setDomicilio`,
    reducer: (state) => ({
      ...state,
      domicilioSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

//Datos Vehiculo
export const setMarca = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setMarca`,
    reducer: (state) => ({
      ...state,
      marcaSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};

export const setAnio = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setAnio`,
    reducer: (state) => ({
      ...state,
      anioSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};
export const setModelo = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setModelo`,
    reducer: (state) => ({
      ...state,
      modeloSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};
export const setCodia = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCodia`,
    reducer: (state) => ({
      ...state,
      codiaSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};
export const setProvincia = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setProvincia`,
    reducer: (state) => ({
      ...state,
      provinciaSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },

      polizaHasChanged: true,
    }),
  };
};
export const setLocalidad = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setLocalidad`,
    reducer: (state) => ({
      ...state,
      localidadSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};
export const setCodigoPostal = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCodigoPostal`,
    reducer: (state) => ({
      ...state,
      codigoPostalSelected: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};
export const setDominio = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setDominio`,
    reducer: (state) => ({
      ...state,
      dominioSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};
export const setNumeroMotor = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setNumeroMotor`,
    reducer: (state) => ({
      ...state,
      numeroMotorSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};
export const setNumeroChasis = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setNumeroChasis`,
    reducer: (state) => ({
      ...state,
      numeroChasisSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setTieneGNC = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setTieneGNC`,
    reducer: (state) => ({
      ...state,
      tieneGNC: { ...value, endosado: true },
      coberturaSelected: { ...state.coberturaSelected, valid: false },
      polizaHasChanged: true,
    }),
  };
};

//certificados
export const setPolizaFileName = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCertificadoPoliza`,
    reducer: (state) => ({
      ...state,
      urlPolizaSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setTarjetaFileName = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setTarjeta`,
    reducer: (state) => ({
      ...state,
      urlTarjetaSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setCedulaMercosurFileName = (
  value: Campo
): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCedulaMercosur`,
    reducer: (state) => ({
      ...state,
      urlCedulaMercosurSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

export const setCuponeraFileName = (value: Campo): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCuponera`,
    reducer: (state) => ({
      ...state,
      urlCuponeraSelected: { ...value, endosado: true },
      polizaHasChanged: true,
    }),
  };
};

//CHECK IF IT IS USED
export const setCurrentPoliza = (
  value: GetPolizaResponse
): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setCurrentPoliza`,
    reducer: (state) => ({
      ...state,
      currentPoliza: value,
    }),
  };
};

export const setSubmitting = (value: boolean): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/setSubmitting`,
    reducer: (state) => ({
      ...state,
      submitting: value,
    }),
  };
};

const getPolizaInited = (): DetallePolizaAction => {
  return {
    type: `${baseDetailPoliza}/getPolizaInited`,
    reducer: (state) => ({
      ...detallePolizaInitialState,
      tabSelected: state.tabSelected,
      isLoading: true,
    }),
  };
};

const getPolizaCompleted = (value: GetPolizaResponse): DetallePolizaAction => {
  const getFormatted = (value: any): Campo => ({
    value: value,
    valid: true,
    focus: false,
  });

  return {
    type: `${baseDetailPoliza}/getPolizaCompleted`,
    reducer: (state) => ({
      ...state,
      currentPoliza: value,
      //Datos Generales
      coberturaSelected: getFormatted(value.coberturaId),
      premioTotalSelected: getFormatted(value.datosPlan.premioTotal),
      formaPagoSelected: getFormatted(value.datosPlan.formaPago),
      //Datos vehiculo
      anioSelected: getFormatted(value.datosVehiculo.anio),
      codiaSelected: getFormatted(value.datosVehiculo.codia),
      codigoPostalSelected: getFormatted(value.datosVehiculo.codigoPostal),
      dominioSelected: getFormatted(value.datosVehiculo.dominio),
      localidadSelected: getFormatted(value.datosVehiculo.nombreLocalidad),
      marcaSelected: getFormatted(value.datosVehiculo.marca),
      modeloSelected: getFormatted(value.datosVehiculo.modelo),
      numeroChasisSelected: getFormatted(value.datosVehiculo.numeroChasis),
      numeroMotorSelected: getFormatted(value.datosVehiculo.numeroMotor),
      // El endoso espera el código de provincia y el detalle solo trae el nombre
      provinciaSelected: getFormatted(null),
      tieneGNC: getFormatted(value.datosVehiculo.tieneGNC),
      //Datos Asegurado
      emailSelected: getFormatted(value.asegurado.email),
      telefonoSelected: getFormatted(
        value.asegurado.telefonoCelular || value.asegurado.telefonoFijo
      ),
      domicilioSelected: getFormatted(value.asegurado.domicilio),
      //certificados
      urlCedulaMercosurSelected: getFormatted(
        value.certificados.ubicacionCedulaMercosur
      ),
      urlCuponeraSelected: getFormatted(value.certificados.ubicacionCuponera),
      urlPolizaSelected: getFormatted(value.certificados.ubicacionPoliza),
      urlTarjetaSelected: getFormatted(value.certificados.ubicacionTarjeta),

      isLoading: false,
      failed: false,
    }),
  };
};

const getPolizaFailed = (): DetallePolizaAction => {
  return {
    type: `${baseDetailPoliza}/getPolizaFailed`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      failed: true,
    }),
  };
};

const postEndosarInited = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/postEndosarInited`,
    reducer: (state) => ({
      ...state,
      isLoading: true,
      polizaUpdated: false,
      failed: false,
    }),
  };
};

const postEndosarCompleted = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/postEndosarCompleted`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      polizaHasChanged: false,
      polizaUpdated: true,
    }),
  };
};

const postEndosarFailed = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/postEndosarFailed`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      failed: true,
    }),
  };
};

export const getPoliza = (id: string) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getPolizaInited());

      const response = await services.api.polizas().getPoliza(id);

      dispatch(getPolizaCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getPolizaFailed());
    }
  };
};

export const postEndosar = (
  polizaId: string,
  data: PostSolicitarEndosoRequest
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(postEndosarInited());

      //const impresos = ["UrlPoliza", "UrlTarjeta", "UrlCedula", "UrlCuponera"];

      /*    await impresos.forEach(async (impreso) => {
        if (data[impreso])
          data[impreso] = (
            await fileService("temp").download(data[impreso])
          ).url;
      });
      */
      await services.api.polizas().postEndosar(polizaId, data);

      dispatch(postEndosarCompleted());
    } catch (error) {
      console.error(error);
      dispatch(postEndosarFailed());
      dispatch(
        queueMessage(
          "Ocurrió un error al actualizar la póliza. Intente nuevamente."
        )
      );
    }
  };
};

const putAplicarEndosoInited = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/putAplicarEndosoInited`,
    reducer: (state) => ({
      ...state,
      isLoading: true,
      polizaUpdated: false,
      failed: false,
    }),
  };
};

const putAplicarEndosoCompleted = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/putAplicarEndosoCompleted`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      polizaHasChanged: false,
      polizaUpdated: true,
    }),
  };
};

const putAplicarEndosoFailed = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/putAplicarEndosoFailed`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      failed: true,
    }),
  };
};

export const putAplicarEndoso = (
  polizaId: string,
  endosoId: string | number,
  data: PutAplicarEndosoRequest
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(putAplicarEndosoInited());

      await services.api.polizas().putAplicarEndoso(polizaId, endosoId, data);

      dispatch(putAplicarEndosoCompleted());
    } catch (error) {
      console.error(error);
      dispatch(putAplicarEndosoFailed());
      dispatch(
        queueMessage(
          "Ocurrió un error al actualizar la póliza. Intente nuevamente."
        )
      );
    }
  };
};

const putRechazarEndosoInited = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/putRechazarEndosoInited`,
    reducer: (state) => ({
      ...state,
      isLoading: true,
      polizaUpdated: false,
      failed: false,
    }),
  };
};

const putRechazarEndosoCompleted = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/putRechazarEndosoCompleted`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      polizaHasChanged: false,
      polizaUpdated: true,
    }),
  };
};

const putRechazarEndosoFailed = (): DetallePolizaAction => {
  return {
    type: `${baseEndosoPoliza}/putRechazarEndosoFailed`,
    reducer: (state) => ({
      ...state,
      isLoading: false,
      failed: true,
    }),
  };
};

export const putRechazarEndoso = (
  polizaId: string,
  endosoId: string | number,
  data?: PutRechazarEndosoRequest
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(putRechazarEndosoInited());

      await services.api.polizas().putRechazarEndoso(polizaId, endosoId, data);

      dispatch(putRechazarEndosoCompleted());
    } catch (error) {
      console.error(error);
      dispatch(putRechazarEndosoFailed());
      dispatch(
        queueMessage(
          "Ocurrió un error al actualizar la póliza. Intente nuevamente."
        )
      );
    }
  };
};
